import { useCallback, useEffect, useState } from 'react'
import { Layout } from '../components/layout/Layout'
import { useAuth } from '../auth/AuthContext'

const API_BASE = import.meta.env.DEV ? '/api' : 'https://asia-northeast1-bluepaper2.cloudfunctions.net/api'

const PLAN_LABEL: Record<string, string> = {
  free: '無料', standard: 'スタンダード', business: 'ビジネス', business_plus: 'ビジネスプラス', enterprise: 'エンタープライズ'
}
const BILLING_LABEL: Record<string, string> = {
  none: '—', trial: 'トライアル', coupon: 'クーポン', stripe: 'Stripe'
}

interface OpsOrg {
  id: string
  name: string
  plan: string
  billing: string
  couponCode: string | null
  members: number
  editors: number
  documents: number | null
  createdAt: string | null
}

interface OpsCoupon {
  code: string
  planType: string
  note: string
  active: boolean
  maxRedemptions: number | null
  redemptions: number
  redeemedOrgs: string[]
  createdAt: string | null
  expiresAt: string | null
}

/**
 * サービス運営者（@bp-g.jp）向け管理画面。
 * 全組織の利用状況とクーポンコードの発行・管理。
 */
export function OpsPage() {
  const { user, firebaseUser } = useAuth()
  const isServiceAdmin = !!user?.email?.endsWith('@bp-g.jp')

  const [orgs, setOrgs] = useState<OpsOrg[]>([])
  const [coupons, setCoupons] = useState<OpsCoupon[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)

  // クーポン発行フォーム
  const [newPlan, setNewPlan] = useState('business')
  const [newCode, setNewCode] = useState('')
  const [newMax, setNewMax] = useState('')
  const [newNote, setNewNote] = useState('')
  const [creating, setCreating] = useState(false)

  const authFetch = useCallback(async (path: string, init?: RequestInit) => {
    if (!firebaseUser) throw new Error('未認証です')
    const token = await firebaseUser.getIdToken()
    return fetch(`${API_BASE}${path}`, {
      ...init,
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', ...(init?.headers || {}) }
    })
  }, [firebaseUser])

  const fetchAll = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [orgsRes, couponsRes] = await Promise.all([
        authFetch('/v1/ops/organizations'),
        authFetch('/v1/ops/coupons')
      ])
      if (!orgsRes.ok) throw new Error(`組織一覧の取得に失敗しました (${orgsRes.status})`)
      if (!couponsRes.ok) throw new Error(`クーポン一覧の取得に失敗しました (${couponsRes.status})`)
      setOrgs((await orgsRes.json()).organizations || [])
      setCoupons((await couponsRes.json()).coupons || [])
    } catch (e) {
      setError(e instanceof Error ? e.message : '読み込みに失敗しました')
    } finally {
      setLoading(false)
    }
  }, [authFetch])

  useEffect(() => {
    if (firebaseUser && isServiceAdmin) fetchAll()
  }, [fetchAll, firebaseUser, isServiceAdmin])

  const createCoupon = async () => {
    setCreating(true)
    setError(null)
    setMessage(null)
    try {
      const res = await authFetch('/v1/ops/coupons', {
        method: 'POST',
        body: JSON.stringify({
          planType: newPlan,
          ...(newCode.trim() ? { code: newCode.trim() } : {}),
          ...(newMax.trim() ? { maxRedemptions: parseInt(newMax, 10) } : {}),
          ...(newNote.trim() ? { note: newNote.trim() } : {})
        })
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || `発行に失敗しました (${res.status})`)
      setMessage(`クーポン「${data.code}」を発行しました`)
      setNewCode('')
      setNewMax('')
      setNewNote('')
      await fetchAll()
    } catch (e) {
      setError(e instanceof Error ? e.message : '発行に失敗しました')
    } finally {
      setCreating(false)
    }
  }

  const toggleCoupon = async (c: OpsCoupon) => {
    try {
      const res = await authFetch(`/v1/ops/coupons/${encodeURIComponent(c.code)}`, {
        method: 'PATCH',
        body: JSON.stringify({ active: !c.active })
      })
      if (!res.ok) throw new Error('更新に失敗しました')
      await fetchAll()
    } catch (e) {
      setError(e instanceof Error ? e.message : '更新に失敗しました')
    }
  }

  if (!isServiceAdmin) {
    return (
      <Layout title="運営管理">
        <div className="alert danger">このページは運営者のみアクセスできます。</div>
      </Layout>
    )
  }

  return (
    <Layout title="運営管理">
      <div className="flex flex-col gap-4" style={{ maxWidth: 1100 }}>
        {error && <div className="alert danger">{error}</div>}
        {message && <div className="alert success">{message}</div>}

        {loading ? (
          <div className="flex items-center gap-2" style={{ padding: 32 }}>
            <span className="spinner" />
            <span className="text-secondary text-sm">読み込み中...</span>
          </div>
        ) : (
          <>
            {/* 組織一覧 */}
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <div className="card-header" style={{ padding: 'var(--space-4) var(--space-5)' }}>
                <h2 className="text-lg font-semibold">組織一覧（{orgs.length}）</h2>
                <button className="btn btn-ghost btn-sm" onClick={fetchAll}>更新</button>
              </div>
              <div className="table-container" style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', minWidth: 760 }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left' }}>組織</th>
                      <th>プラン</th>
                      <th>課金状態</th>
                      <th>メンバー</th>
                      <th>編集者</th>
                      <th>議事録数</th>
                      <th>作成日</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orgs.map((o) => (
                      <tr key={o.id}>
                        <td>
                          <div className="text-sm font-medium">{o.name}</div>
                          <div className="text-xs text-tertiary">{o.id}</div>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <span className="badge badge-info">{PLAN_LABEL[o.plan] || o.plan}</span>
                        </td>
                        <td className="text-sm" style={{ textAlign: 'center' }}>
                          {BILLING_LABEL[o.billing] || o.billing}
                          {o.couponCode && <div className="text-xs text-tertiary">{o.couponCode}</div>}
                        </td>
                        <td className="text-sm" style={{ textAlign: 'center' }}>{o.members}</td>
                        <td className="text-sm" style={{ textAlign: 'center' }}>{o.editors}</td>
                        <td className="text-sm" style={{ textAlign: 'center' }}>{o.documents ?? '—'}</td>
                        <td className="text-xs text-secondary" style={{ textAlign: 'center' }}>
                          {o.createdAt ? new Date(o.createdAt).toLocaleDateString('ja-JP') : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* クーポン管理 */}
            <div className="card">
              <div className="card-header">
                <h2 className="text-lg font-semibold">クーポンコード</h2>
              </div>

              {/* 発行フォーム */}
              <div className="flex items-end gap-3 flex-wrap" style={{ marginBottom: 16 }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">プラン</label>
                  <select className="form-select" value={newPlan} onChange={(e) => setNewPlan(e.target.value)}>
                    <option value="standard">スタンダード</option>
                    <option value="business">ビジネス</option>
                    <option value="business_plus">ビジネスプラス</option>
                    <option value="enterprise">エンタープライズ</option>
                  </select>
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">コード（省略時は自動生成）</label>
                  <input className="form-input" style={{ width: 160, textTransform: 'uppercase' }} value={newCode} onChange={(e) => setNewCode(e.target.value)} placeholder="BP-XXXXXXXX" />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">利用回数上限</label>
                  <input className="form-input" style={{ width: 100 }} type="number" min={1} value={newMax} onChange={(e) => setNewMax(e.target.value)} placeholder="無制限" />
                </div>
                <div className="form-group" style={{ marginBottom: 0, flex: '1 1 160px' }}>
                  <label className="form-label">メモ</label>
                  <input className="form-input" value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="例: ◯◯社向け" />
                </div>
                <button className="btn btn-primary" disabled={creating} onClick={createCoupon}>
                  {creating ? <span className="spinner sm" /> : '発行'}
                </button>
              </div>

              {/* 一覧 */}
              <div className="table-container" style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', minWidth: 640 }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left' }}>コード</th>
                      <th>プラン</th>
                      <th>利用</th>
                      <th>状態</th>
                      <th style={{ textAlign: 'left' }}>メモ</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {coupons.length === 0 && (
                      <tr><td colSpan={6} className="text-sm text-secondary" style={{ padding: 16 }}>クーポンはまだありません</td></tr>
                    )}
                    {coupons.map((c) => (
                      <tr key={c.code}>
                        <td className="text-sm font-medium" style={{ fontFamily: 'monospace' }}>{c.code}</td>
                        <td style={{ textAlign: 'center' }}>
                          <span className="badge badge-info">{PLAN_LABEL[c.planType] || c.planType}</span>
                        </td>
                        <td className="text-sm" style={{ textAlign: 'center' }}>
                          {c.redemptions}{c.maxRedemptions ? ` / ${c.maxRedemptions}` : ''}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          {c.active
                            ? <span className="badge badge-success">有効</span>
                            : <span className="badge badge-neutral">無効</span>}
                        </td>
                        <td className="text-xs text-secondary">{c.note}</td>
                        <td style={{ textAlign: 'center' }}>
                          <button className="btn btn-ghost btn-sm" onClick={() => toggleCoupon(c)}>
                            {c.active ? '無効化' : '有効化'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  )
}
